import type { TokenizerExtension } from 'marked';
import { RENDERABLE_REF_BODY } from '@cabal/shared/domain/short-id/pattern.js';
import {
  CREFERENCE_TOKEN_NAME,
  type CreferenceToken,
} from './reference-types';

// The inner capture excludes BOTH brackets, not just the closer. With a bare
// `[^\]]+` the greedy run swallowed the extra opener in `[[[f1]]]` and emitted
// a chip for the non-sid `[f1`, stranding a `]` behind it; refusing the opener
// makes the tokenizer skip a character and claim the well-formed `[[f1]]`
// inside instead.
const CREFERENCE_PATTERN = /^\[\[([^[\]]+)\]\]/;

// Every prompt surface that TEACHES the ref syntax spells it inside a code span
// (`[[…]]`), so models sometimes emit the token that way too. Markdown makes a
// code span literal, which would render the reference as raw `[[f1]]` text —
// the one place a ref silently stops being a ref. Claim it back.
//
// The grammar gate is what makes this safe: only a code span whose ENTIRE body
// is a bracketed, well-formed sid is stolen. Real code keeps its span, because
// `[[1,2],[3,4]]` and `[[foo]]` do not match the sid grammar. Fenced code
// blocks are block-level and never reach an inline tokenizer at all.
//
// The gate is case-insensitive so it agrees with the case folding below —
// `[[F1]]` is as much a reference inside a code span as outside one.
const FENCED_CREFERENCE_PATTERN = new RegExp(
  `^(\`+)[ \\t]*\\[\\[(${RENDERABLE_REF_BODY})\\]\\][ \\t]*\\1(?!\`)`,
  'i',
);

const CREFERENCE_START_MARKERS = ['[[', '`[['];
const MARKED_INLINE_TOKEN_LEVEL = 'inline';

export function createCreferenceTokenizerExtension(): TokenizerExtension {
  return {
    name: CREFERENCE_TOKEN_NAME,
    level: MARKED_INLINE_TOKEN_LEVEL,
    start(src) {
      return CREFERENCE_START_MARKERS.reduce<number | undefined>(
        (earliest, marker) => {
          const index = src.indexOf(marker);
          if (index < 0) {
            return earliest;
          }
          return earliest === undefined ? index : Math.min(earliest, index);
        },
        undefined,
      );
    },
    tokenizer(src) {
      const match = FENCED_CREFERENCE_PATTERN.exec(src)
        ?? CREFERENCE_PATTERN.exec(src);
      if (!match) {
        return undefined;
      }

      // Group 1 is the bare sid on the plain pattern and the backtick run on
      // the fenced one, where the sid moves to group 2. Case is folded because
      // every sid the DB mints is a lowercase prefix plus digits — there is no
      // case-colliding sid for `[[F1]]` to mean other than `f1`.
      const shortId = (match[2] ?? match[1] ?? '').trim().toLowerCase();
      if (!shortId) {
        return undefined;
      }

      const raw = match[0];
      const token: CreferenceToken = {
        type: CREFERENCE_TOKEN_NAME,
        raw,
        text: raw,
        shortId,
      };

      return token;
    },
  };
}
